import React, { useState, useEffect } from "react";
import { Card, Row, Col, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { fetchProgramsByPagination } from "../services/ProgramService";
import { getCachedLatestPrograms } from "../utils/homePageCache";
import { IMAGE_BASE } from "../Config";
import { getPlayImageUrl, handleImageError } from "../utils/imageUtils";

import ProgramData from "../models/ProgramData";

interface HomePlayCardProps {
  productions?: ProgramData[];
  useCached?: boolean;
}

const HomePlayCard: React.FC<HomePlayCardProps> = ({ productions, useCached = false }) => {
  const navigate = useNavigate();
  const [programs, setPrograms] = useState<ProgramData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        setLoading(true);
        if (productions) {
          // Use provided productions from props
          setPrograms(productions);
        } else {
          // Fetch productions if not provided
          let response;
          if (useCached) {
            response = await getCachedLatestPrograms(1, 6, "createdAt");
          } else {
            response = await fetchProgramsByPagination(1, 6, "createdAt");
            // Store in cache for future use
            const { cacheSet } = await import('../utils/cacheUtil');
            await cacheSet(`latest_programs_1_6_createdAt`, response, 21600); // 6 hours TTL
          }
          setPrograms(response.programs || []);
        }
      } catch (error) {
        setPrograms([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPrograms();
  }, [productions]);

  const handlePlayClick = (id: string) => {
    navigate(`/play/${id}`);
  };

  const formatDuration = (hours?: number, minutes?: number) => {
    if (!hours && !minutes) return "Duration TBA";
    const h = hours ? `${hours}h` : "";
    const m = minutes ? `${minutes}m` : "";
    return `${h} ${m}`.trim();
  };

  if (loading) {
    return (
      <Row className="g-4">
        {[...Array(3)].map((_, index) => (
          <Col key={index} lg={4} md={6}>
            <Card className="h-100 shadow-sm">
              <div className="bg-light" style={{ height: "250px" }}></div>
              <Card.Body>
                <div className="bg-light rounded" style={{ height: "20px", marginBottom: "10px" }}></div>
                <div className="bg-light rounded" style={{ height: "60px" }}></div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    );
  }

  return (
    <Row className="g-4">
      {programs.length === 0 ? (
        <Col xs={12} className="text-center py-5">
          <i className="bi bi-play-circle display-1 text-muted mb-3"></i>
          <h5 className="text-muted">No productions available</h5>
        </Col>
      ) : (
        programs.map((play) => (
          <Col key={play._id} lg={4} md={6}>
            <Card 
              className="h-100 shadow-sm border-0" 
              onClick={() => handlePlayClick(play._id)}
              style={{ cursor: "pointer", transition: "transform 0.2s" }}
              onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-5px)"}
              onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
            >
              <div className="position-relative">
                <Card.Img 
                  variant="top" 
                  src={getPlayImageUrl(play.image1)} 
                  style={{ height: "250px", objectFit: "cover" }}
                  onError={(e) => handleImageError(e, 'PLAY')}
                />
                {play.isFeatured && (
                  <Badge bg="warning" text="dark" className="position-absolute top-0 start-0 m-2">
                    <i className="bi bi-star-fill me-1"></i>Featured
                  </Badge>
                )}
              </div>
              <Card.Body className="d-flex flex-column">
                <Card.Title className="fw-bold mb-2">{play.title}</Card.Title>
                <Card.Text className="text-muted flex-grow-1" style={{ fontSize: "0.9rem" }}>
                  {play.description && play.description.length > 100 
                    ? `${play.description.substring(0, 100)}...` 
                    : play.description || "An exciting theatrical production awaiting your discovery."}
                </Card.Text>
                <div className="d-flex justify-content-between align-items-center mt-auto pt-2">
                  <small className="text-muted">
                    <i className="bi bi-clock me-1"></i>
                    {formatDuration(play.durationHours, play.durationMinutes)}
                  </small>
                  <small className="text-success fw-bold">
                    Check Schedules <i className="bi bi-calendar-event"></i>
                  </small>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))
      )}
    </Row>
  );
};

export default HomePlayCard;
